import { methods, REMOTE_PROCTORING_ROOT } from '../api/ApiConstants';
import { idverificationApiTimeout } from './IdVerificationConstants';
import apiTools from '../api/ApiTools';
import sessionReader from '../../store/readHelpers/SessionReader';

function createRealRequestCallback(url, postData) {
    return function realRequestCallback(lambdaRequestProperties) {
        const { idCheckToken, inRequest } = lambdaRequestProperties;
        const request = apiTools.makeJsonPostRequest(
            url,
            {
                ...inRequest,
                ...postData,
            },
        );

        if (!request.init.headers) {
            request.init.headers = {};
        }

        request.init.headers.Authorization = `Bearer ${idCheckToken}`;

        return apiTools.timeoutFetch(
            idverificationApiTimeout,
            request.url,
            request.init,
        );
    };
}

class LambdaRequestPropertiesError extends Error {
    constructor(message, response) {
        super(message);
        this.response = response;
    }

    toString() {
        const responseJsonString = JSON.stringify(this.response);

        return `Error in GetLatestRemoteProctoringSessionForUserTestSetup response: ${this.message}\nresponse = ${responseJsonString}`;
    }
}

/**
 * isTokenExpired attempts to parse the JSON Web Token, find the expiration time
 * and ensure it isn't expired.
 *
 * It tries to be robust: if the token can't be parsed, it considers the token
 * valid so a parsing error won't block the user.
 */
function isTokenExpired(token) {
    const tokenSplit = token.replace(/-/g, '+').replace(/_/g, '/').split('.');

    if (tokenSplit.length !== 3) {
        return false;
    }

    try {
        const payload = JSON.parse(window.atob(tokenSplit[1]));
        const { exp: expiration } = payload;
        const now = Math.floor(Date.now() / 1000);

        return expiration <= now;
    } catch (theError) {
        return false;
    }
}

class ApiWithSessionService {
    constructor() {
        this.lambdaRequestProperties = null;
        this.fetchPromise = null;
    }

    setLambdaRequestProperties = (response) => {
        this.fetchPromise = null;

        if (
            !Object.prototype.hasOwnProperty.call(response, 'idCheckToken')
            || !Object.prototype.hasOwnProperty.call(response, 'bucket')
            || !Object.prototype.hasOwnProperty.call(response, 'region')
        ) {
            throw new LambdaRequestPropertiesError(
                'Fields bucket, region and idCheckToken are required.',
                response,
            );
        }

        const {
            bucket: bucketName,
            idCheckToken,
            region,
            idVerificationApiUrl,
        } = response;

        if (typeof idCheckToken !== 'string' || idCheckToken.length < 1) {
            throw new LambdaRequestPropertiesError(
                'idCheckToken must be a string containing a JSON Web Token.',
                response,
            );
        }

        if (typeof bucketName !== 'string' || bucketName.length < 1) {
            throw new LambdaRequestPropertiesError(
                'bucket must be a string containing Amazon S3 bucket name.',
                response,
            );
        }

        if (typeof region !== 'string' || region.length < 1) {
            throw new LambdaRequestPropertiesError(
                'region must be a string containing Amazon S3 region name.',
                response,
            );
        }

        this.lambdaRequestProperties = {
            idCheckToken,
            inRequest: {
                s3BucketDetails: {
                    bucketName,
                    region,
                },
            },
        };
        this.idVerificationApiUrl = idVerificationApiUrl;

        return this.lambdaRequestProperties;
    };

    reset = () => {
        this.fetchPromise = null;
        this.lambdaRequestProperties = null;
    };

    fetchRequestProperties(apiUrl = null) {
        if (
            this.lambdaRequestProperties !== null
            && !isTokenExpired(this.lambdaRequestProperties.idCheckToken)
        ) {
            return Promise.resolve(this.lambdaRequestProperties);
        } else if (this.fetchPromise === null) {
            let requestUrl = null;

            if (typeof apiUrl === 'string') {
                requestUrl = apiUrl;
            } else {
                const assessmentRunId = sessionReader.getAssessmentRunId();

                const params = new URLSearchParams();
                params.set('assessmentRunId', assessmentRunId);

                requestUrl = apiTools.prependDomain(`${REMOTE_PROCTORING_ROOT}${methods.ID_VERIFICATION_REQUEST_PROPERTIES}?${params.toString()}`);
            }

            this.fetchPromise = apiTools.executeRequestWithRetry(
                apiTools.makeGetRequest(requestUrl),
            ).then(this.setLambdaRequestProperties);

            /**
             * If the request fails, give the opportunity for the caller to
             * attempt again later.
             */
            this.fetchPromise.catch(this.reset);
        }

        return this.fetchPromise;
    }

    fetch(postData) {
        return this.fetchRequestProperties().then((lambdaRequestProperties) => {
            return createRealRequestCallback(this.idVerificationApiUrl, postData)(lambdaRequestProperties);
        });
    }
}

const singleton = new ApiWithSessionService();

export default singleton;
